import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useRef } from 'react';
import { AuthProvider } from '@lucid/auth';
import { Alert } from '@lucid/lego';
import { PortalCoreProvider } from '@lucid/portal-core';
import { auth0Config } from './config/auth0';
import { portalConfig } from './config/portal.config';
import { router } from './routes';
import { authDebugInfo } from './utils/authDebug';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  // Guard against StrictMode double-invocation of onRedirectCallback.
  // Auth0 auth codes are one-time use — a second exchange returns invalid_grant.
  const callbackHandled = useRef(false);

  const onRedirectCallback = (appState?: { returnTo?: string }) => {
    if (callbackHandled.current) return;
    callbackHandled.current = true;

    authDebugInfo('Auth0 onRedirectCallback triggered', {
      returnTo: appState?.returnTo ?? '/',
      currentPathname: window.location.pathname,
      hasCode: window.location.search.includes('code='),
      hasState: window.location.search.includes('state='),
    });

    router.navigate(appState?.returnTo ?? '/', { replace: true });
  };

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider
        domain={auth0Config.domain}
        clientId={auth0Config.clientId}
        authorizationParams={auth0Config.authorizationParams}
        cacheLocation={auth0Config.cacheLocation}
        useRefreshTokens={auth0Config.useRefreshTokens}
        onRedirectCallback={onRedirectCallback}
      >
        <PortalCoreProvider config={portalConfig}>
          <Alert position="top-center" theme="dark" dataTestId="global-alert" />
          <RouterProvider router={router} />
        </PortalCoreProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
